import { QuizTopicPageTemplate } from "@/components/QuizTopicPageTemplate";

const tocItems = [
  { id: "why-video-game-quizzes", title: "Why Video Game Quizzes?" },
  { id: "popular-game-quizzes", title: "Popular Game Quizzes" },
  { id: "quiz-types", title: "Types of Gaming Quizzes" },
  { id: "challenge-friends", title: "Challenge Your Friends" },
];

const quizResources = [
  { name: "HowStuffWorks Fortnite Quiz", url: "https://electronics.howstuffworks.com/fortnite-quiz.htm", description: "Test your Fortnite knowledge" },
  { name: "Super Mario Quiz", url: "https://electronics.howstuffworks.com/super-mario-quiz.htm", description: "How well do you know Mario?" },
  { name: "JetPunk Pokemon Quiz", url: "https://www.jetpunk.com/quizzes/original-pokemon-quiz", description: "Name all 151 original Pokemon" },
  { name: "Roblox Quiz", url: "https://electronics.howstuffworks.com/roblox-quiz.htm", description: "Run, Jump & Climb Roblox Quiz" },
  { name: "Britannica Video Games", url: "https://www.britannica.com/quiz/true-or-false-video-games-through-the-ages", description: "History of gaming trivia" },
];

const relatedTopics = [
  { name: "Pop Culture Quizzes", path: "/quizzes/pop-culture-quizzes/", description: "Movies, TV, and entertainment" },
  { name: "Fun Quizzes", path: "/quizzes/fun-quizzes-to-take/", description: "Quizzes when you're bored" },
  { name: "Trivia Quizzes", path: "/quizzes/trivia-quizzes/", description: "Test general knowledge" },
];

const VideoGameQuizzes = () => {
  return (
    <QuizTopicPageTemplate
      title="Video Game Quizzes: Test Your Gaming Knowledge"
      metaTitle="Video Game Quizzes | Gaming Trivia & Tests 2025"
      metaDescription="Challenge your video game knowledge with the best gaming quizzes. From Fortnite to Pokemon, Mario to Roblox - prove you're the ultimate gamer."
      keywords="video game quizzes, gaming trivia, fortnite quiz, pokemon quiz, mario quiz, roblox quiz, gaming knowledge test"
      canonicalUrl="/quizzes/video-game-quizzes/"
      category="Gaming"
      publishDate="January 12, 2025"
      readTime="5 min read"
      tocItems={tocItems}
      leadParagraph="Think you know everything about video games? From classic Nintendo titles to the latest battle royales, video game quizzes are the ultimate way to prove your gaming expertise and maybe learn a few things about gaming history you didn't know."
      quizResources={quizResources}
      relatedTopics={relatedTopics}
    >
      <section id="why-video-game-quizzes" className="mb-12">
        <h2 className="text-3xl font-display font-bold mb-6">
          Why <span className="text-gradient">Video Game Quizzes?</span>
        </h2>
        <p className="text-lg leading-relaxed text-foreground/90 mb-6">
          Video games are more than just entertainment—they're a cultural phenomenon spanning decades. From the pixelated adventures of the 80s to today's photorealistic open worlds, gaming has evolved into one of the biggest industries in entertainment.
        </p>
        <p className="text-lg leading-relaxed text-foreground/90 mb-6">
          Testing your video game knowledge is a fun way to:
        </p>
        <ul className="space-y-4 mb-6">
          <li className="flex gap-3 text-lg text-foreground/90">
            <span className="text-primary font-bold">•</span>
            <span><strong>Relive memories:</strong> Remember games from your childhood</span>
          </li>
          <li className="flex gap-3 text-lg text-foreground/90">
            <span className="text-primary font-bold">•</span>
            <span><strong>Learn gaming history:</strong> Discover the origins of your favorite franchises</span>
          </li>
          <li className="flex gap-3 text-lg text-foreground/90">
            <span className="text-primary font-bold">•</span>
            <span><strong>Compete with friends:</strong> Settle the debate on who's the real gamer</span>
          </li>
          <li className="flex gap-3 text-lg text-foreground/90">
            <span className="text-primary font-bold">•</span>
            <span><strong>Discover new games:</strong> Learn about titles you might want to try</span>
          </li>
        </ul>
      </section>

      <section id="popular-game-quizzes" className="mb-12">
        <h2 className="text-3xl font-display font-bold mb-6">
          Popular <span className="text-gradient">Game Quizzes</span>
        </h2>
        
        <h3 className="text-2xl font-display font-semibold mt-8 mb-4">Pokemon Quizzes</h3>
        <p className="text-lg leading-relaxed text-foreground/90 mb-6">
          Can you name all 151 original Pokemon? The classic <a href="https://www.jetpunk.com/quizzes/original-pokemon-quiz" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">JetPunk Original 151 Pokemon Quiz</a> challenges players to recall every Pokemon from Generation 1 in 10 minutes. For true fans, there are extended quizzes covering all generations with over 900 Pokemon to remember.
        </p>

        <h3 className="text-2xl font-display font-semibold mt-8 mb-4">Fortnite Knowledge Tests</h3>
        <p className="text-lg leading-relaxed text-foreground/90 mb-6">
          Are you a noob or a pro? The <a href="https://electronics.howstuffworks.com/fortnite-quiz.htm" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">HowStuffWorks Fortnite Quiz</a> tests everything from weapon stats and map knowledge to season lore and crossover events. Perfect for battle royale enthusiasts.
        </p>

        <h3 className="text-2xl font-display font-semibold mt-8 mb-4">Super Mario Trivia</h3>
        <p className="text-lg leading-relaxed text-foreground/90 mb-6">
          The mustached plumber has been around since 1981. <a href="https://electronics.howstuffworks.com/super-mario-quiz.htm" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">The Super Mario Quiz</a> covers Mario's adventures through the Mushroom Kingdom—from power-ups to boss battles.
        </p>

        <h3 className="text-2xl font-display font-semibold mt-8 mb-4">Roblox Challenges</h3>
        <p className="text-lg leading-relaxed text-foreground/90 mb-6">
          With millions of games on the platform, <a href="https://electronics.howstuffworks.com/roblox-quiz.htm" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Roblox quizzes</a> test your knowledge of popular experiences, game mechanics, and Roblox history.
        </p>
      </section>

      <section id="quiz-types" className="mb-12">
        <h2 className="text-3xl font-display font-bold mb-6">
          Types of <span className="text-gradient">Gaming Quizzes</span>
        </h2>
        <div className="grid md:grid-cols-2 gap-4 mb-6">
          <a href="https://play.howstuffworks.com/quiz/can-you-match-the-video-game-character-to-the-right-franchise" target="_blank" rel="noopener noreferrer" className="p-4 rounded-xl bg-card/50 border border-border/50 hover:border-primary/50 transition-all group">
            <h4 className="font-semibold mb-2 group-hover:text-primary">Character Matching</h4>
            <p className="text-sm text-muted-foreground">Match video game characters to their franchises</p>
          </a>
          <div className="p-4 rounded-xl bg-card/50 border border-border/50">
            <h4 className="font-semibold mb-2">Screenshot Guessing</h4>
            <p className="text-sm text-muted-foreground">Name the game from a single image</p>
          </div>
          <div className="p-4 rounded-xl bg-card/50 border border-border/50">
            <h4 className="font-semibold mb-2">Music Recognition</h4>
            <p className="text-sm text-muted-foreground">Identify games by their iconic soundtracks</p>
          </div>
          <a href="https://www.britannica.com/quiz/true-or-false-video-games-through-the-ages" target="_blank" rel="noopener noreferrer" className="p-4 rounded-xl bg-card/50 border border-border/50 hover:border-primary/50 transition-all group">
            <h4 className="font-semibold mb-2 group-hover:text-primary">True or False</h4>
            <p className="text-sm text-muted-foreground">Video games through the ages</p>
          </a>
          <div className="p-4 rounded-xl bg-card/50 border border-border/50">
            <h4 className="font-semibold mb-2">Release Dates</h4>
            <p className="text-sm text-muted-foreground">Test your gaming history knowledge</p>
          </div>
          <div className="p-4 rounded-xl bg-card/50 border border-border/50">
            <h4 className="font-semibold mb-2">Developer Trivia</h4>
            <p className="text-sm text-muted-foreground">Know which studios made which games</p>
          </div>
        </div>
      </section>

      <section id="challenge-friends" className="mb-12">
        <h2 className="text-3xl font-display font-bold mb-6">
          Challenge <span className="text-gradient">Your Friends</span>
        </h2>
        <p className="text-lg leading-relaxed text-foreground/90 mb-6">
          Gaming is more fun with friends, and so are gaming quizzes. With CSW, you can create custom video game quizzes and challenge your squad. Who knows the most about Zelda lore? Who can name every Call of Duty game in order?
        </p>
        <p className="text-lg leading-relaxed text-foreground/90 mb-6">
          Create quizzes about your favorite games, share them with your gaming group, and settle once and for all who has the most gaming knowledge.
        </p>
      </section>
    </QuizTopicPageTemplate>
  );
};

export default VideoGameQuizzes;
